/** The app: levels, the world controller, and the wiring between the editor, the presenters, and the URL. */

import { createSandboxLevel } from "../../game/levels.ts";
import type { Level, SandboxOptions } from "../../game/levels.ts";
import { INSTANT_RUN_MAX_SIMULATED_SECONDS, driveInstantly } from "../../game/instant-run.ts";
import type { InstantRunHandle } from "../../game/instant-run.ts";
import { chapter2Levels } from "../../game/chapter2.ts";
import type { Chapter2Level } from "../../game/chapter2.ts";
import { tutorialLevels } from "../../game/tutorial.ts";
import type { TutorialLevel } from "../../game/tutorial.ts";
import type { RandomSeed } from "../../game/random.ts";
import { createWorld } from "../../game/world.ts";
import type { World } from "../../game/world.ts";
import type { AnimationFrameRequester, WorldController } from "../../game/world-controller.ts";
import { t } from "../../i18n/index.ts";
import type { CodeEditor } from "../../ui/editor.ts";
import {
  elevatorFloorButtonLabel,
  elevatorLabel,
  floorCallDownLabel,
  floorCallUpLabel,
} from "../../ui/templates.ts";
import type { SeedLinkData } from "../../ui/templates.ts";
import { chapter2Address, LEVEL_KEY, SANDBOX_LEVEL } from "./model/route.ts";
import type { RouteParams } from "./model/route.ts";
import { evaluateLevelTier, nextTierHint } from "#entities/level-tier/index.ts";
import { readBestChapter1Tiers, recordChapter1Tier } from "#entities/chapter1-level/index.ts";
import { readBestChapter2Tiers, recordChapter2Tier } from "#entities/chapter2-level/index.ts";
import {
  TUTORIAL_CLEARED_TIER,
  readClearedTutorialLevels,
  recordClearedTutorialLevel,
} from "#entities/tutorial-level/model/progress.ts";
import { presentSpeedStepper, speedStepperTemplate } from "#features/adjust-speed/index.ts";
import {
  clampTimeScale,
  decreasedTimeScale,
  increasedTimeScale,
  isFastestTimeScale,
} from "#features/adjust-speed/model/time-scale.ts";
import { DEFAULT_CODE_SLOT } from "#features/manage-code-slots/model/code-slots.ts";
import type { CodeSlot } from "#features/manage-code-slots/model/code-slots.ts";
import { presentRunControls, runButtonsTemplate } from "#features/run-simulation/index.ts";
import { clearChildren, query, queryAll } from "#shared/lib/dom.ts";
import { createParamsUrl } from "#shared/lib/route-query.ts";
import type { RouteQuery } from "#shared/lib/route-query.ts";
import { isUsableSeed } from "#shared/lib/seed.ts";
import { presentBuildingStage } from "#widgets/building-stage/index.ts";
import type { EditorPanePresenter } from "#widgets/editor-pane/index.ts";
import { presentGoalBar } from "#widgets/goal-bar/index.ts";
import type { GoalBarPresenter } from "#widgets/goal-bar/index.ts";
import { levelSwitcherTemplate, presentLevelSwitcher } from "#widgets/level-switcher/index.ts";
import type {
  LevelLinkTarget,
  LevelMenuInput,
  LevelSelection,
  LevelSwitcherPresenter,
} from "#widgets/level-switcher/index.ts";
import { presentLevelBriefing } from "#widgets/level-briefing/index.ts";
import { presentStatsPanel } from "#widgets/stats-panel/index.ts";
import type { StatsPanelPresenter } from "#widgets/stats-panel/index.ts";
import { presentTutorial } from "#widgets/tutorial-panel/index.ts";
import { presentVerdictToast } from "#widgets/verdict-toast/index.ts";

declare global {
  interface Window {
    /** The world being played, exposed to the console and the player's program; writable since nothing here is scored. */
    world: World | undefined;
  }
}

/** Where the chosen simulation speed is remembered between visits. */
export const TIME_SCALE_STORAGE_KEY = "elevatorTimeScale";

/** Where the player's last-used seed is remembered between runs and visits; never a seed a level supplied. */
export const SEED_STORAGE_KEY = "elevatorSeed";

/** Stands in for a program that failed to compile, so the world does nothing instead of throwing and masking the real error. */
const NO_OP_CODE = {
  init: (): void => undefined,
  update: (): void => undefined,
};

/** The link of the end-of-run card, whose words the last level of the track rewrites. */
const FEEDBACK_LINK_SELECTOR = ".verdict a";

/** Resolves a hash URL to an absolute one, for logging to the console. */
function absoluteUrl(hash: string): string {
  return new URL(hash, window.location.href).href;
}

/** Class on `<html>` that hides everything except the world. */
export const FULLSCREEN_CLASS = "fullscreen-demo";

/**
 * Selectors {@link relabelWorld} uses to rename building parts after a language
 * change; must match the classes `entities/floor` and `entities/elevator` draw with.
 */
const FLOOR_SELECTOR = ".floor";
const CALL_UP_SELECTOR = "button.up";
const CALL_DOWN_SELECTOR = "button.down";
const ELEVATOR_SELECTOR = ".elevator";
const FLOOR_BUTTON_SELECTOR = ".buttonpress";

/** Empties several containers. */
export function clearAll(elements: readonly Element[]): void {
  for (const element of elements) {
    clearChildren(element);
  }
}

/**
 * Whether the focused element sits inside one of these containers. Call before
 * emptying them — once a node is gone, `activeElement` has already reverted to
 * `<body>`.
 */
export function containsFocus(elements: readonly Element[]): boolean {
  const active = document.activeElement;
  return (
    active !== null && elements.some((element) => element !== active && element.contains(active))
  );
}

/** The run controls' markup: the start/pause/resume and start-over buttons, plus the speed stepper. */
export function controlsTemplate(): string {
  return runButtonsTemplate() + speedStepperTemplate();
}

/** What the run controls need in order to draw and drive themselves. */
export interface ControlsPresenterOptions {
  /** The controller being driven, consulted for `isPaused` and `timeScale`. */
  readonly worldController: Pick<WorldController, "isPaused" | "timeScale">;
  /** Whether the run on screen is over; a function, not a value, since this row outlives the world it reports on. */
  readonly levelEnded: () => boolean;
  /** Whether the run on screen has already ticked, so the button offers to resume rather than to start. */
  readonly runStarted: () => boolean;
  /** Whether the speed control is on its instant stop rather than a time scale. */
  readonly instantSpeed: () => boolean;
  /** Whether the instant stop is offered on the run on screen at all. */
  readonly instantAvailable: () => boolean;
  /** Called when the start/pause/resume button is pressed. */
  readonly onStartStop: () => void;
  /** Called when "Start over" is pressed. */
  readonly onStartOver: () => void;
  /** Called when the `+` button is pressed. */
  readonly onTimeScaleIncrease: () => void;
  /** Called when the `-` button is pressed. */
  readonly onTimeScaleDecrease: () => void;
  /** Whether a headless "run instantly" crunch is under way. */
  readonly instantRunInProgress: () => boolean;
}

/** The rendered run controls. */
export interface ControlsPresenter {
  /** Relabels the start button and the speed; call after anything that could have moved that state. */
  update(): void;

  /** Puts focus on the start button, to recover it after a redraw empties the region it was in. */
  focusStartStop(): void;
}

/** Draws the run controls and wires them up; called once, from {@link App}'s constructor. */
export function presentControls(
  parent: HTMLElement,
  options: ControlsPresenterOptions,
): ControlsPresenter {
  parent.innerHTML = controlsTemplate();

  // Forward through closures, not the callbacks themselves: `options` is a live object the app keeps mutating.
  const runControls = presentRunControls(parent, {
    worldController: options.worldController,
    levelEnded: () => options.levelEnded(),
    runStarted: () => options.runStarted(),
    instantSpeed: () => options.instantSpeed(),
    instantRunInProgress: () => options.instantRunInProgress(),
    onStartStop: () => {
      options.onStartStop();
    },
    onStartOver: () => {
      options.onStartOver();
    },
  });

  const speedStepper = presentSpeedStepper(parent, {
    worldController: options.worldController,
    instantSpeed: () => options.instantSpeed(),
    instantAvailable: () => options.instantAvailable(),
    instantRunInProgress: () => options.instantRunInProgress(),
    onTimeScaleIncrease: () => {
      options.onTimeScaleIncrease();
    },
    onTimeScaleDecrease: () => {
      options.onTimeScaleDecrease();
    },
  });

  const presenter: ControlsPresenter = {
    update(): void {
      runControls.update();
      speedStepper.update();
    },

    focusStartStop(): void {
      runControls.focusStartStop();
    },
  };
  // Before anything can take focus, so a screen reader announces "Start" rather than an unnamed button.
  presenter.update();
  return presenter;
}

/**
 * Renames a building already drawn, in the language active now, without
 * tearing down the run. Matches by position (floor, car, button order), so it
 * must stay in sync with however `entities/floor` and `entities/elevator` draw it.
 */
export function relabelWorld(parent: HTMLElement): void {
  for (const [level, floor] of queryAll(FLOOR_SELECTOR, parent).entries()) {
    // Optional: `entities/floor` draws no "up" lamp on the roof or "down" in the lobby.
    query(CALL_UP_SELECTOR, floor)?.setAttribute("aria-label", floorCallUpLabel(level));
    query(CALL_DOWN_SELECTOR, floor)?.setAttribute("aria-label", floorCallDownLabel(level));
  }
  for (const [index, elevator] of queryAll(ELEVATOR_SELECTOR, parent).entries()) {
    elevator.setAttribute("aria-label", elevatorLabel(index));
    for (const [floorNum, button] of queryAll(FLOOR_BUTTON_SELECTOR, elevator).entries()) {
      button.setAttribute("aria-label", elevatorFloorButtonLabel(floorNum));
    }
  }
}

/** Hides everything except the world, for the `#fullscreen` demo mode. */
export function setDemoFullscreen(enabled: boolean): void {
  document.documentElement.classList.toggle(FULLSCREEN_CLASS, enabled);
}

/** The page regions the app draws into. */
export interface AppElements {
  /** The run controls' mount; unlike the regions below, never emptied and redrawn between runs — only relabeled. */
  readonly controls: HTMLElement;
  /** Where the learning track's panel goes; empty (and hidden by the stylesheet) on every other route. */
  readonly tutorial: HTMLElement;
  /** Where `widgets/level-switcher`'s trigger and popover go; drawn once, like {@link controls}, and only relabeled after. */
  readonly levelSwitcher: HTMLElement;
  /** Where `widgets/goal-bar` goes: the current level's meters and tier popover. */
  readonly goalBar: HTMLElement;
  /** Where `widgets/building-stage` draws the building. */
  readonly world: HTMLElement;
  /** Where `widgets/stats-panel` draws the run's figures. */
  readonly stats: HTMLElement;
  /** Where `widgets/verdict-toast` draws the end-of-level overlay. */
  readonly feedback: HTMLElement;
}

/** Everything the app needs to run. */
export interface AppOptions {
  /** The page regions to draw into. */
  readonly elements: AppElements;
  /** The player's editor. */
  readonly editor: CodeEditor;
  /** The already-built `widgets/editor-pane` presenter; built before this class since {@link editor}'s view needs its mount to exist. */
  readonly editorPane: EditorPanePresenter;
  /** The controller driving the simulation. */
  readonly worldController: WorldController;
  /** Chapter one's levels, in order. */
  readonly chapter1Levels: readonly Level[];
  /** Where the time scale and track progress are remembered; defaults to `localStorage`, not the player's program. */
  readonly storage?: Storage;
  /** Schedules simulation frames; defaults to `requestAnimationFrame`. */
  readonly requestAnimationFrame?: AnimationFrameRequester;
  /** Called whenever the seed link might have changed, with the value {@link App.currentSeedLink} would return right after. */
  readonly onSeedChange: (seed: SeedLinkData | null) => void;
}

/** A learning-track level and its position in `tutorialLevels` ("Lesson N" for the player). */
export interface TutorialRun {
  /** The level being played. */
  readonly level: TutorialLevel;
  /** Its position in `tutorialLevels`, counted from zero. */
  readonly index: number;
}

/** A chapter-two level and its position in `chapter2Levels`; the menu numbers it as chapter two. */
export interface Chapter2Run {
  /** The level being played. */
  readonly level: Chapter2Level;
  /** Its position in `chapter2Levels`, counted from zero. */
  readonly index: number;
}

/** Reads the remembered time scale, or `undefined` if there is none stored. */
export function readStoredTimeScale(storage: Storage): number | undefined {
  let stored: string | null;
  try {
    stored = storage.getItem(TIME_SCALE_STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (stored === null) {
    return undefined;
  }
  const timeScale = Number.parseFloat(stored);
  return Number.isFinite(timeScale) ? clampTimeScale(timeScale) : undefined;
}

/** Reads the last-played seed, validated the same way `#seed=` is; an invalid value is treated as nothing stored. */
export function readStoredSeed(storage: Storage): string | undefined {
  let stored: string | null;
  try {
    stored = storage.getItem(SEED_STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (stored === null || !isUsableSeed(stored)) {
    return undefined;
  }
  return stored;
}

/** Runs the game. */
export class App {
  /** Chapter one's levels, in the order they are played. */
  readonly chapter1Levels: readonly Level[];
  /** The controller driving the simulation. */
  readonly worldController: WorldController;
  /** The world currently being played, once a level has started. */
  world: World | undefined = undefined;
  /** Index of the level currently played; left alone during a sandbox run, since it's the restart target, not what's on screen. */
  currentChapter1Index = 0;

  readonly #elements: AppElements;
  readonly #editor: CodeEditor;
  readonly #storage: Storage;
  readonly #requestAnimationFrame: AnimationFrameRequester;
  /** See {@link AppOptions.onSeedChange}. */
  readonly #onSeedChange: (seed: SeedLinkData | null) => void;
  /** The run controls, drawn once in the constructor and never tied to a run. */
  readonly #controls: ControlsPresenter;
  /** The level switcher, drawn once in the constructor; reports on every level at once, not tied to a run. */
  readonly #levelSwitcher: LevelSwitcherPresenter;
  /** The editor pane, drawn once in the constructor; its codetools act on the editor across every run. */
  readonly #editorPane: EditorPanePresenter;
  /** The goal bar for the run on screen, or `undefined` before the first one; rebuilt by every {@link #startRun}, not by a language change. */
  #goalBar: GoalBarPresenter | undefined = undefined;
  /** The stats panel for the run on screen, or `undefined` before the first one; rebuilt by every {@link #startRun}. */
  #statsPanel: StatsPanelPresenter | undefined = undefined;
  /** The code slot open in the editor for the run on screen, in-memory only; set by whichever `start…` began it. */
  #currentSlot: CodeSlot = DEFAULT_CODE_SLOT;
  /** The parameters of the URL the current level was started from. */
  #query: RouteQuery = new Map<string, string>();
  /** The building the sandbox is running, or `undefined` for a level. */
  #sandbox: SandboxOptions | undefined = undefined;
  /** The learning-track level being played, or `undefined`; mutually exclusive with the other run kinds via {@link #clearSpecialRuns}. */
  #tutorial: TutorialRun | undefined = undefined;
  /** The chapter-two level being played, or `undefined`; behaves like {@link #tutorial} but records a medal on a win. */
  #chapter2: Chapter2Run | undefined = undefined;
  /** The seed every run is built from, or `null` to draw one; read from the URL only — see {@link #startRun} for its precedence. */
  #seed: string | null = null;
  /** What is on screen, or `undefined` before the first run — the level itself, not its description, so {@link relocalize} can re-ask it. */
  #run: { readonly level: Level; readonly chapter1Index: number | null } | undefined = undefined;
  /** Whether the run on screen was won, or `undefined` while still going; the outcome, not the overlay's words, so it can redraw. */
  #outcome: boolean | undefined = undefined;
  /** Whatever the player's program last threw; wrapped since `throw undefined` is legal and must stay distinguishable from no error. */
  #codeError: { readonly thrown: unknown } | undefined = undefined;
  /** The headless crunch in progress, or `undefined`; its own private controller, canceled at the top of every {@link #startRun}. */
  #instantRunHandle: InstantRunHandle | undefined = undefined;
  /** Whether the speed control is on its `∞x` stop; its own field, not a value of `timeScale`, which an `Infinity` would freeze. */
  #instantSpeed = false;

  constructor(options: AppOptions) {
    this.#elements = options.elements;
    this.#editor = options.editor;
    this.#editorPane = options.editorPane;
    this.worldController = options.worldController;
    this.chapter1Levels = options.chapter1Levels;
    this.#storage = options.storage ?? localStorage;
    this.#requestAnimationFrame =
      options.requestAnimationFrame ??
      ((callback): void => {
        requestAnimationFrame(callback);
      });
    this.#onSeedChange = options.onSeedChange;

    // Drawn once; every question it asks closures over `this`, so it works before a world exists.
    this.#controls = presentControls(this.#elements.controls, {
      worldController: this.worldController,
      levelEnded: () => this.world?.levelEnded === true,
      runStarted: () => this.#runHasBegun,
      instantSpeed: () => this.#instantSpeed,
      instantAvailable: () => this.canRunInstantly,
      instantRunInProgress: () => this.#instantRunHandle !== undefined,
      // On `∞x` there's nothing to pause or resume, so both just run a fresh crunch.
      onStartStop: () => {
        if (this.#instantSpeed) {
          this.runInstantly();
        } else {
          this.startStopOrRestart();
        }
      },
      onStartOver: () => {
        this.#runFromStart();
      },
      // The `+`/`-` arrows enter and leave the `∞x` stop; neither touches `timeScale` — see {@link #instantSpeed}.
      onTimeScaleIncrease: () => {
        if (isFastestTimeScale(this.worldController.timeScale)) {
          // Guarded, not just dimmed: a disabled button only stops a click, not a state change.
          if (this.canRunInstantly) {
            this.#setInstantSpeed(true);
          }
        } else {
          this.worldController.setTimeScale(increasedTimeScale(this.worldController.timeScale));
        }
      },
      onTimeScaleDecrease: () => {
        if (this.#instantSpeed) {
          this.#setInstantSpeed(false);
        } else {
          this.worldController.setTimeScale(decreasedTimeScale(this.worldController.timeScale));
        }
      },
    });

    // Drawn once; `getInput` is read fresh on every `update()` since tiers move on every win.
    this.#elements.levelSwitcher.innerHTML = levelSwitcherTemplate();
    this.#levelSwitcher = presentLevelSwitcher(this.#elements.levelSwitcher, {
      getInput: () => this.#levelMenuInput(),
    });

    // Subscribed once for the app's lifetime; pausing raises this event too, relabeling the start button.
    this.worldController.on("timescale_changed", () => {
      this.#storeTimeScale();
      this.#controls.update();
    });
    this.worldController.on("usercode_error", (e) => {
      console.log("World raised code error", e);
      this.#editor.trigger("usercode_error", e);
    });

    // The same Mod-Enter as the "Start over" button, reaching the editor instead of the page.
    this.#editor.on("apply_code", () => {
      this.#runFromStart();
    });
    this.#editor.on("code_success", () => {
      this.#codeError = undefined;
      this.#editorPane.clearError();
    });
    this.#editor.on("usercode_error", (error) => {
      this.#codeError = { thrown: error };
      this.#editorPane.showError(error, this.#editor.getCode());
    });
  }

  /** Everything `widgets/level-switcher` needs to draw its popover; tile links deliberately drop `seed` since a link names a building, not a run. */
  #levelMenuInput(): LevelMenuInput {
    return {
      chapter1Levels: this.chapter1Levels,
      tutorialLevels,
      chapter2Levels,
      bestChapter1Tiers: readBestChapter1Tiers(this.#storage),
      bestChapter2Tiers: readBestChapter2Tiers(this.#storage),
      clearedTutorialLevels: readClearedTutorialLevels(this.#storage),
      selection: this.#levelSelection(),
      buildHref: (target) => this.#levelHref(target),
    };
  }

  /** What is on screen, shaped for `widgets/level-switcher`; falls back to {@link currentChapter1Index} before the first run starts. */
  #levelSelection(): LevelSelection {
    const tutorial = this.#tutorial;
    if (tutorial !== undefined) {
      return { kind: "tutorial", index: tutorial.index };
    }
    const chapter2 = this.#chapter2;
    if (chapter2 !== undefined) {
      return { kind: "chapter2", index: chapter2.index };
    }
    if (this.isPlayingSandbox) {
      return { kind: "sandbox" };
    }
    return { kind: "chapter1", index: this.#run?.chapter1Index ?? this.currentChapter1Index };
  }

  /** Turns a tile's {@link LevelLinkTarget} into the URL it links to, carrying the speed and every other unknown key across. */
  #levelHref(target: LevelLinkTarget): string {
    switch (target.kind) {
      case "chapter1": {
        return createParamsUrl(this.#query, { [LEVEL_KEY]: target.number, seed: null });
      }
      case "chapter2": {
        return createParamsUrl(this.#query, {
          [LEVEL_KEY]: chapter2Address(target.number),
          seed: null,
        });
      }
      case "tutorial": {
        return createParamsUrl(this.#query, { [LEVEL_KEY]: target.levelId, seed: null });
      }
      case "sandbox": {
        return createParamsUrl(this.#query, { [LEVEL_KEY]: SANDBOX_LEVEL, seed: null });
      }
    }
  }

  /** The seed of a run and its URL, read off the world (not {@link #seed}), so every kind of run has one. */
  #seedLink(world: World, chapter1Index: number | null): SeedLinkData | null {
    if (world.seed === null) {
      // Only reachable for a world built with a ready-made random stream; the app never does that.
      return null;
    }
    const seed = String(world.seed);
    return { seed, url: this.#seedHref(seed, chapter1Index) };
  }

  /** The address of this building played on `seed`; always names the level explicitly, even when the URL already does. */
  #seedHref(seed: string, chapter1Index: number | null): string {
    const at = chapter1Index === null ? {} : { [LEVEL_KEY]: chapter1Index + 1 };
    return createParamsUrl(this.#query, { ...at, seed });
  }

  /** Plays `seed` on the building already on screen; navigates rather than restarts in place, so the address bar names the run. */
  playSeed(seed: string): void {
    window.location.hash = this.#seedHref(seed, this.#run?.chapter1Index ?? null);
  }

  /** The seed line for whatever is on screen; a snapshot, not a subscription — {@link AppOptions.onSeedChange} is the push side. */
  get currentSeedLink(): SeedLinkData | null {
    const world = this.world;
    const run = this.#run;
    if (world === undefined || run === undefined) {
      return null;
    }
    return this.#seedLink(world, run.chapter1Index);
  }

  /**
   * The seed the next run is built from, and whether the level supplied it rather than the
   * player. The player's own comes first — the URL's `#seed=`, then the one remembered from
   * last visit — and the two blocks that pin a seed fall back to theirs when there is none.
   */
  #seedForRun(): { seed: RandomSeed | undefined; fromLevel: boolean } {
    const playerSeed = this.#seed ?? readStoredSeed(this.#storage);
    if (playerSeed !== undefined) {
      return { seed: playerSeed, fromLevel: false };
    }
    // `undefined` outside those blocks, where it asks for a fresh draw.
    const levelSeed = this.#tutorial?.level.seed ?? this.#chapter2?.level.seed;
    return { seed: levelSeed, fromLevel: levelSeed !== undefined };
  }

  /** Remembers a seed as this player's own, for the next run and the next visit. */
  #storeSeed(seed: string): void {
    try {
      this.#storage.setItem(SEED_STORAGE_KEY, seed);
    } catch {
      // A browser that refuses storage should not stop the game.
    }
  }

  /** Remembers the current time scale for the next visit. */
  #storeTimeScale(): void {
    try {
      this.#storage.setItem(TIME_SCALE_STORAGE_KEY, String(this.worldController.timeScale));
    } catch {
      // A browser that refuses storage should not stop the game.
    }
  }

  /** Whether what is on screen is the sandbox rather than a numbered level. */
  get isPlayingSandbox(): boolean {
    return this.#sandbox !== undefined;
  }

  /** Whether the run on screen is one a crunch could finish; false only for the sandbox, whose condition never resolves. */
  get canRunInstantly(): boolean {
    return !this.isPlayingSandbox;
  }

  /** Starts whatever is on screen again, from the beginning; dispatches on the special-run fields, not `currentChapter1Index` alone. */
  #restart(autoStart = false): void {
    const tutorial = this.#tutorial;
    const chapter2 = this.#chapter2;
    const sandbox = this.#sandbox;
    const slot = this.#currentSlot;
    if (tutorial !== undefined) {
      this.startTutorial(tutorial.index, autoStart, slot);
    } else if (chapter2 !== undefined) {
      this.startChapter2Level(chapter2.index, autoStart, slot);
    } else if (sandbox === undefined) {
      this.startChapter1Level(this.currentChapter1Index, autoStart, slot);
    } else {
      this.startSandbox(sandbox, autoStart, slot);
    }
  }

  /**
   * Runs what is on screen from the beginning, reading the editor fresh: a crunch on the `∞x`
   * stop, where a real-time run means nothing, and otherwise a restart that starts itself
   * (`autoStart`), unlike the start button's own offer after a run ends.
   */
  #runFromStart(): void {
    if (this.#instantSpeed) {
      this.runInstantly();
    } else {
      this.#restart(true);
    }
  }

  /** Whether the run on screen has begun: a world that has ticked at all, rather than one still waiting for its first frame. */
  get #runHasBegun(): boolean {
    return (this.world?.elapsedTime ?? 0) > 0;
  }

  /** Starts, pauses or restarts the simulation, depending on where it is. */
  startStopOrRestart(): void {
    if (this.world?.levelEnded === true) {
      this.#restart();
      return;
    }
    // Start restarts, reading the editor fresh, only while paused and still at zero; once ticked, only "Start over" may replace it.
    if (this.worldController.isPaused && !this.#runHasBegun) {
      this.#restart(true);
      return;
    }
    this.worldController.setPaused(!this.worldController.isPaused);
  }

  /** Runs whatever is on screen again, headlessly, always `autoStart` since a crunch has nothing visible to wait for. No-op before the first run. */
  runInstantly(): void {
    const run = this.#run;
    if (run === undefined || !this.canRunInstantly) {
      return;
    }
    this.#startRun(run.level, run.chapter1Index, true, true);
  }

  /** Closes a crunch out: marks it finished, draws the building in its final state, and relabels the row. */
  #endInstantRun(world: World): void {
    this.#instantRunHandle = undefined;
    presentBuildingStage(this.#elements.world, world);
    this.#controls.update();
  }

  /** Moves the speed control on and off its instant stop; redraws explicitly since this never goes through `setTimeScale`. */
  #setInstantSpeed(instant: boolean): void {
    this.#instantSpeed = instant;
    this.#controls.update();
  }

  /** Acts on a route: starts the level it names, in precedence order — learning track, chapter two, sandbox, then a numbered level. */
  handleRoute(params: RouteParams, query: RouteQuery): void {
    this.#query = query;
    this.#seed = params.seed;
    setDemoFullscreen(params.fullscreen);
    this.worldController.setTimeScale(params.timeScale);
    if (params.tutorialIndex !== null) {
      this.startTutorial(params.tutorialIndex);
    } else if (params.chapter2Index !== null) {
      this.startChapter2Level(params.chapter2Index);
    } else if (params.sandbox === null) {
      this.startChapter1Level(params.chapter1Index);
    } else {
      this.startSandbox(params.sandbox);
    }
  }

  /** Tears the current level down and starts another one. */
  startChapter1Level(
    chapter1Index: number,
    autoStart = false,
    slot: CodeSlot = DEFAULT_CODE_SLOT,
  ): void {
    const level = this.chapter1Levels[chapter1Index];
    if (level === undefined) {
      throw new RangeError(`No level with index ${String(chapter1Index)}`);
    }
    this.#clearSpecialRuns();
    this.#currentSlot = slot;
    this.#editor.openChapter1Buffer(chapter1Index, slot);
    this.currentChapter1Index = chapter1Index;
    this.#startRun(level, chapter1Index, autoStart);
  }

  /** Switches the editor to another code slot without disturbing the run in progress; a no-op when that slot is already open. */
  selectCodeSlot(slot: CodeSlot): void {
    if (slot === this.#currentSlot) {
      return;
    }
    this.#currentSlot = slot;
    this.#openSlot(slot);
    this.#editorPane.update();
  }

  /**
   * Opens `slot`'s buffer for whatever is on screen, dispatching on the
   * special-run fields in the same order {@link #restart} does. Every run kind
   * has three slots; only the key they are stored under differs.
   */
  #openSlot(slot: CodeSlot): void {
    const named = this.#tutorial?.level ?? this.#chapter2?.level;
    if (named !== undefined) {
      this.#editor.openNamedLevelBuffer(named.id, named.startingCode, slot);
    } else if (this.isPlayingSandbox) {
      this.#editor.openPlayerBuffer(slot);
    } else {
      this.#editor.openChapter1Buffer(this.currentChapter1Index, slot);
    }
  }

  /** The code slot currently open in the editor. */
  get currentCodeSlot(): CodeSlot {
    return this.#currentSlot;
  }

  /** Tears the current level down and starts a sandbox run; the building comes entirely from the URL, so a bookmark reproduces it. */
  startSandbox(
    options: SandboxOptions,
    autoStart = false,
    slot: CodeSlot = DEFAULT_CODE_SLOT,
  ): void {
    this.#clearSpecialRuns();
    this.#sandbox = options;
    this.#currentSlot = slot;
    this.#editor.openPlayerBuffer(slot);
    this.#startRun(createSandboxLevel(options), null, autoStart);
  }

  /** Tears the current run down and starts a level of the learning track; opens its buffer before {@link #startRun} compiles it. */
  startTutorial(
    tutorialIndex: number,
    autoStart = false,
    slot: CodeSlot = DEFAULT_CODE_SLOT,
  ): void {
    const level = tutorialLevels[tutorialIndex];
    if (level === undefined) {
      throw new RangeError(`No tutorial level with index ${String(tutorialIndex)}`);
    }
    this.#clearSpecialRuns();
    this.#tutorial = { level, index: tutorialIndex };
    this.#currentSlot = slot;
    // The player's own attempt if they left one in this slot, else the level's starting code.
    this.#editor.openNamedLevelBuffer(level.id, level.startingCode, slot);
    this.#startRun(level, null, autoStart);
  }

  /** Starts a chapter two level like {@link startTutorial} does, except `startingCode` is never optional — its levels assume mechanics chapter one's don't. */
  startChapter2Level(
    chapter2Index: number,
    autoStart = false,
    slot: CodeSlot = DEFAULT_CODE_SLOT,
  ): void {
    const level = chapter2Levels[chapter2Index];
    if (level === undefined) {
      throw new RangeError(`No chapter two level with index ${String(chapter2Index)}`);
    }
    this.#clearSpecialRuns();
    this.#chapter2 = { level, index: chapter2Index };
    this.#currentSlot = slot;
    this.#editor.openNamedLevelBuffer(level.id, level.startingCode, slot);
    this.#startRun(level, null, autoStart);
  }

  /** Forgets whichever special run was in effect; call before setting a new one, or the switcher will answer for a stale run. */
  #clearSpecialRuns(): void {
    this.#sandbox = undefined;
    this.#tutorial = undefined;
    this.#chapter2 = undefined;
  }

  /** The level of the learning track on screen, or `undefined` otherwise; read-only since {@link startTutorial} is how it changes. */
  get tutorial(): TutorialRun | undefined {
    return this.#tutorial;
  }

  /** The chapter two level on screen, or `undefined` otherwise. */
  get chapter2(): Chapter2Run | undefined {
    return this.#chapter2;
  }

  /** Builds a world for a level, draws it, and hands it to the controller; `chapter1Index` is `null` for the sandbox. */
  #startRun(level: Level, chapter1Index: number | null, autoStart: boolean, instant = false): void {
    // Cancel any earlier crunch so it can't go on ticking a world nothing on screen points at anymore.
    this.#instantRunHandle?.cancel();
    this.#instantRunHandle = undefined;
    // Drop the instant stop when entering a run that can't use it (the sandbox).
    if (this.#instantSpeed && !this.canRunInstantly) {
      this.#instantSpeed = false;
    }
    this.world?.unWind();
    const { seed: chosenSeed, fromLevel } = this.#seedForRun();
    const world = createWorld(level.options, chosenSeed);
    this.world = world;
    window.world = world;
    const seed = this.#seedLink(world, chapter1Index);
    if (seed !== null) {
      if (!fromLevel) {
        // Remembered for next time; a seed the level supplied is not the player's own to keep.
        this.#storeSeed(seed.seed);
      }
      // Logged so a run worth repeating can be found after the fact.
      console.log(t("game.seed.console", { seed: seed.seed, url: absoluteUrl(seed.url) }));
    }

    // Checked before these regions are emptied, since a removed node drops focus to `<body>`; restored at the end of this method.
    const focusWasDestroyed = containsFocus([
      this.#elements.world,
      this.#elements.feedback,
      this.#elements.tutorial,
    ]);
    clearAll([this.#elements.world, this.#elements.feedback]);
    this.#run = { level, chapter1Index };
    this.#outcome = undefined;
    this.#statsPanel = presentStatsPanel(this.#elements.stats, world);
    this.#goalBar = presentGoalBar(this.#elements.goalBar, world, {
      level,
      getVerdict: () => level.condition.evaluate(world),
    });
    this.#redrawForLevel();
    // Skipped for a crunch — the whole point is that nothing gets drawn mid-run.
    if (!instant) {
      presentBuildingStage(this.#elements.world, world);
    }

    world.on("stats_changed", () => {
      const conditionStatus = level.condition.evaluate(world);
      // A crunch that outruns INSTANT_RUN_MAX_SIMULATED_SECONDS without a verdict counts as a loss; an animated run has no ceiling.
      const levelStatus =
        conditionStatus ??
        (instant && world.elapsedTime >= INSTANT_RUN_MAX_SIMULATED_SECONDS ? false : null);
      if (levelStatus === null) {
        return;
      }
      world.levelEnded = true;
      if (instant) {
        // Not `worldController.setPaused` — a crunch drives its own private
        // controller.
        this.#endInstantRun(world);
      } else {
        this.worldController.setPaused(true);
      }
      // Recorded here, not in `#showOutcome` (which `relocalize` also calls), so a language change can't re-record progress.
      const tutorial = this.#tutorial;
      const chapter2 = this.#chapter2;
      if (levelStatus && tutorial !== undefined) {
        recordClearedTutorialLevel(this.#storage, tutorial.level.id);
        this.#levelSwitcher.update();
      } else if (levelStatus && chapter2 !== undefined) {
        // A medal, not a cleared flag: a demo level that grades nothing earns gold, keyed by id since `chapter1Index` is null here.
        const tier = evaluateLevelTier(true, world, chapter2.level.tiers);
        if (tier !== null) {
          recordChapter2Tier(this.#storage, chapter2.level.id, tier);
          this.#levelSwitcher.update();
        }
      } else if (levelStatus && chapter1Index !== null) {
        const tier = evaluateLevelTier(true, world, level.tiers);
        if (tier !== null) {
          recordChapter1Tier(this.#storage, chapter1Index, tier);
          this.#levelSwitcher.update();
        }
      }
      this.#showOutcome(levelStatus);
    });

    const codeObj = this.#editor.getCodeObj();
    if (instant) {
      // `onController`, not the returned handle: a program whose `init` throws on the first tick can finish inside `driveInstantly`.
      const handle = driveInstantly(world, codeObj ?? NO_OP_CODE, {
        onController: (controller) => {
          controller.on("usercode_error", (e) => {
            console.log("World raised code error", e);
            this.#editor.trigger("usercode_error", e);
            // A crunch's private controller never unpauses, so an error ends it.
            this.#endInstantRun(world);
          });
        },
      });
      // Skip storing a handle for a crunch that already finished or errored inside `driveInstantly`, above.
      if (!world.levelEnded && !handle.controller.isPaused) {
        this.#instantRunHandle = handle;
      }
    } else {
      this.worldController.start(
        world,
        codeObj ?? NO_OP_CODE,
        this.#requestAnimationFrame,
        autoStart,
      );
    }
    // After the controller: `start` sets pause by assignment, raising no event,
    // so the label needs an explicit refresh here.
    this.#controls.update();
    // After the label, so a screen reader announces the right button name.
    if (focusWasDestroyed) {
      this.#controls.focusStartStop();
    }
  }

  /** Redraws everything tied to the run on screen except the goal bar itself: the switcher, the editor pane, and the tutorial panel. */
  #redrawForLevel(): void {
    if (this.#run === undefined) {
      return;
    }
    this.#goalBar?.update();
    this.#levelSwitcher.update();
    this.#editorPane.update();
    this.#drawTutorialPanel();
    this.#onSeedChange(this.currentSeedLink);
  }

  /**
   * Draws whichever card the level on screen has earned — the learning track's
   * lesson panel, or a chapter two level's briefing card — into the same shared
   * region, emptying it when the level has neither.
   */
  #drawTutorialPanel(): void {
    const tutorial = this.#tutorial;
    if (tutorial !== undefined) {
      presentTutorial(this.#elements.tutorial, { levelIndex: tutorial.index });
      return;
    }
    // Read fresh here (not looked up by id) so a language change picks up the catalog's current text.
    const card = this.#chapter2?.level.card;
    if (card !== undefined) {
      presentLevelBriefing(this.#elements.tutorial, card);
      return;
    }
    clearChildren(this.#elements.tutorial);
  }

  /**
   * Draws the end-of-level card and remembers the outcome, so {@link relocalize}
   * can redraw the same verdict in another language.
   */
  #showOutcome(won: boolean): void {
    this.#outcome = won;
    const tutorial = this.#tutorial;
    if (tutorial !== undefined) {
      this.#showTutorialOutcome(tutorial, won);
      return;
    }
    const run = this.#run;
    const world = this.world;
    const chapter1Index = run?.chapter1Index ?? null;
    // Recomputed rather than cached, so a `relocalize` redraw stays in sync without a
    // field of its own. Not gated on `chapter1Index`: that is null for a chapter two level
    // too, and its card owes the same medal its tile shows. The sandbox never gets here.
    const tier =
      won && run !== undefined && world !== undefined
        ? (evaluateLevelTier(true, world, run.level.tiers) ?? undefined)
        : undefined;
    presentVerdictToast(this.#elements.feedback, {
      won,
      title: won ? t("game.feedback.success.title") : t("game.feedback.failure.title"),
      message: won ? t("game.feedback.success.message") : t("game.feedback.failure.message"),
      // Recomputed for the same reason as `tier` above.
      hint:
        tier !== undefined && run !== undefined && world !== undefined
          ? nextTierHint(run.level.tiers, tier, world)
          : "",
      // No link after a failure or on the last level; seed dropped since it
      // belongs to the run just completed.
      url:
        won && chapter1Index !== null && chapter1Index + 1 < this.chapter1Levels.length
          ? createParamsUrl(this.#query, { [LEVEL_KEY]: chapter1Index + 2, seed: null })
          : "",
      tier,
    });
  }

  /**
   * Puts everything already drawn into the language active now, without tearing
   * down the run in progress. Figures shown via `Intl` and anything the player
   * typed into the editor are deliberately left alone.
   */
  relocalize(): void {
    // Before the pane: it compares the on-screen program to the old language's starter, so the pane must not see the new one yet.
    this.#editor.relocalize();
    // Unconditional: the controls and pane are on screen before any run starts.
    this.#controls.update();
    this.#editorPane.update();
    const world = this.world;
    if (world !== undefined) {
      this.#redrawForLevel();
      this.#statsPanel?.update();
      world.trigger("stats_display_changed");
      relabelWorld(this.#elements.world);
    }
    if (this.#outcome !== undefined) {
      this.#showOutcome(this.#outcome);
    }
    if (this.#codeError !== undefined) {
      this.#editorPane.showError(this.#codeError.thrown, this.#editor.getCode());
    }
  }

  /**
   * Draws the end-of-run overlay for a track level. A win relabels the link to
   * name the next lesson (never "Next level"); a win on the last level swaps in
   * the finish message. Nothing is recorded here — {@link #startRun} did that.
   */
  #showTutorialOutcome(tutorial: TutorialRun, won: boolean): void {
    const isLastLevel = tutorial.index + 1 >= tutorialLevels.length;
    const nextLevel = tutorialLevels[tutorial.index + 1];
    const finished = won && isLastLevel;
    presentVerdictToast(this.#elements.feedback, {
      won,
      title: finished
        ? t("tutorial.finish.title")
        : won
          ? t("game.feedback.success.title")
          : t("game.feedback.failure.title"),
      message: finished
        ? t("tutorial.finish.message")
        : won
          ? t("game.feedback.success.message")
          : t("game.feedback.failure.message"),
      // The track grades nothing, so a win is already gold and no star is left to name.
      hint: "",
      // Seed dropped since it belongs to the lesson just completed, as on a numbered level.
      url: finished
        ? createParamsUrl(this.#query, { [LEVEL_KEY]: 1, seed: null })
        : won && nextLevel !== undefined
          ? createParamsUrl(this.#query, { [LEVEL_KEY]: nextLevel.id, seed: null })
          : "",
      // The same constant the track's tiles read, so the card and the tile a win
      // lights up can't disagree about which medal it was.
      tier: won ? TUTORIAL_CLEARED_TIER : undefined,
    });
    if (won) {
      this.#relabelFeedbackLink(
        finished ? t("tutorial.finish.toLevels") : t("tutorial.finish.nextLevel"),
      );
    }
  }

  /**
   * Replaces the link's text node, leaving its caret icon alone. A missing
   * link is the ordinary case — there's none after a loss.
   */
  #relabelFeedbackLink(words: string): void {
    const link = this.#elements.feedback.querySelector(FEEDBACK_LINK_SELECTOR);
    const text = link?.firstChild;
    if (text?.nodeType === Node.TEXT_NODE) {
      text.textContent = `${words} `;
    }
  }
}
